import { haversineKm } from '@routellers/utils/dist/route-warnings';
import { AttractionStopBriefCard } from './route/attraction-stop-brief';
import { FieldHistoryPanel } from './field-history-panel';
import {
    ActionIcon,
    Box,
    Button,
    Divider,
    Group,
    NumberInput,
    Select,
    Stack,
    Tabs,
    Text,
    Switch,
    Tooltip,
    Textarea,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AttractionRouteRoleEnum } from '@routellers/types/dist/city-attraction/enum/attraction-route-role.enum';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { CityReadyRouteRO } from '@routellers/types/dist/city-ready-route/city-ready-route.ro';
import { ReadyRouteGuideTypeEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-guide-type.enum';
import { ReadyRouteMobilityEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-mobility.enum';
import { ReadyRouteStatusEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-status.enum';
import { ReadyRouteStopKindEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-stop-kind.enum';
import { ReadyRouteMapPoolAttractionRO } from '@routellers/types/dist/city-ready-route/ready-route-map-pool.ro';
import { ReadyRouteStopContentOverride } from '@routellers/types/dist/city-ready-route/ready-route-stop-content';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    IconAlertTriangleFilled,
    IconGripVertical,
    IconSearch,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { useAuth } from '../providers/auth-context';
import { isRouteTextOnly } from '../utils/admin-permissions';
import {
    EditorFooter,
    EntityEditorShell,
    LanguageChips,
    MediaDropzone,
    Section,
} from '../components/editor';
import { enumLabel, enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { AttractionEditor } from './attraction-editor';
import { EstablishmentEditor } from './establishment-editor';
import { AuthorSelect } from './author-select';
import { CitySearchSelect } from './city-search-select';
import { AttractionPhotos } from './route/attraction-photos';
import { ConstructorMap } from './route/constructor-map';
import { StaffNotesPanel } from './staff-notes-panel';

type Props = {
    opened: boolean;
    onClose: () => void;
    routeId: number | null;
    onSaved: () => void;
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
    initialCityId?: number | null;
};

type LocaleDraft = {
    title: string;
    teaser: string;
    description: string;
    transitNote: string;
};

type StopContentLocales = Partial<Record<Languages, ReadyRouteStopContentOverride>>;

/** Recorded narration for a stop. */
type StopAudioDraft = {
    guideAudioUrl: string | null;
    guideAudioDurationSec: number | null;
};

type StopAudioLocales = Partial<Record<Languages, StopAudioDraft>>;

type PoolPlace = Omit<ReadyRouteMapPoolAttractionRO, 'attractionId'> & {
    attractionId: number | null;
    establishmentId?: number | null;
    placeKey: string;
};
const placeKeyOf = (s: { attractionId: number | null; establishmentId?: number | null }) =>
    s.establishmentId != null
        ? `establishment:${s.establishmentId}`
        : `attraction:${s.attractionId}`;

type DraftStop = {
    key: string;
    /** Persisted stop id — required so save updates in place and never wipes siblings. */
    id?: number;
    order: number;
    kind: ReadyRouteStopKindEnum;
    attractionId: number | null;
    establishmentId?: number | null;
    optionalBreak?: boolean;
    fieldExcluded?: boolean;
    breakMinutes?: number | null;
    lat: number | null;
    lng: number | null;
    /** Второй вход. Первая точка маршрута им не помечается. */
    alternateStart: boolean;
    routeRoleOverride: AttractionRouteRoleEnum | null;
    title?: string;
    reviewStatus?: string;
    /** Per-language stop content overrides (hook / guideTalk). */
    locales: StopContentLocales;
    /** Per-language audio for those texts, uploaded or generated. */
    audio: StopAudioLocales;
};

const emptyLocale = (): LocaleDraft => ({
    title: '',
    teaser: '',
    description: '',
    transitNote: '',
});
const emptyStopContent = (): ReadyRouteStopContentOverride => ({
    hook: '',
    guideTalk: '',
});

const emptyStopAudio = (): StopAudioDraft => ({
    guideAudioUrl: null,
    guideAudioDurationSec: null,
});

const hasStopAudio = (audio?: StopAudioDraft | null) => !!audio?.guideAudioUrl;

const formatAudioLength = (durationSec?: number | null) => {
    if (!durationSec) return '—';

    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Длительность нужна плееру, чтобы показать таймлайн и рассчитать маршрут, а
 * загруженный человеком файл её не сообщает — читаем из метаданных браузером.
 */
const probeAudioDurationSec = (url: string) =>
    new Promise<number | null>((resolve) => {
        const probe = new Audio();

        const done = (value: number | null) => {
            probe.onloadedmetadata = null;
            probe.onerror = null;
            resolve(value);
        };

        probe.onloadedmetadata = () =>
            done(Number.isFinite(probe.duration) ? Math.round(probe.duration) : null);
        probe.onerror = () => done(null);
        probe.preload = 'metadata';
        probe.src = url;
    });

const hasStopOverride = (content?: ReadyRouteStopContentOverride | null) =>
    !!content?.hook?.trim() || !!content?.guideTalk?.trim();

const stopHasAnyOverride = (locales: StopContentLocales) =>
    Object.values(locales).some((content) => hasStopOverride(content));

const poolLocale = (attraction: PoolPlace | null | undefined, language: Languages) =>
    attraction?.locales?.find((l) => l.language === language) || null;

/**
 * Только строка запрошенного языка: подстановка чужого языка в подписи точки
 * читается как готовый перевод и прячет от редактора незаполненную локаль.
 */
const attractionKnownFor = (attraction: PoolPlace | null | undefined, language: Languages) =>
    poolLocale(attraction, language)?.knownFor?.trim() || null;

const attractionTitle = (
    attraction: PoolPlace | null | undefined,
    language: Languages,
    fallback?: string | null,
) =>
    poolLocale(attraction, language)?.title?.trim() ||
    poolLocale(attraction, CONTENT_BASE_LANGUAGE)?.title?.trim() ||
    fallback ||
    attraction?.title ||
    null;

const normalizeSearch = (value: string) =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const attractionSearchBlob = (attraction: PoolPlace) =>
    normalizeSearch(
        [
            attraction.title,
            ...(attraction.locales || []).flatMap((locale) => [
                locale.title,
                locale.knownFor,
                locale.hook,
                locale.guideTalk,
            ]),
        ]
            .filter(Boolean)
            .join('\n'),
    );

let stopKeySeq = 1;
const nextStopKey = () => `stop-${stopKeySeq++}`;

export const RouteEditor = ({
    opened,
    onClose,
    routeId,
    onSaved,
    onCreated,
    onDeleted,
    initialCityId,
}: Props) => {
    const { t } = useTranslation();
    const { admin } = useAuth();
    const isCreate = routeId == null;
    const textOnly = !isCreate && isRouteTextOnly(admin);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [tab, setTab] = useState('meta');
    const [dirty, setDirty] = useState(false);

    const [cityId, setCityId] = useState<number | null>(initialCityId ?? null);
    const [cityTitle, setCityTitle] = useState('');
    const [country, setCountry] = useState<CountriesEnum | null>(null);
    const [status, setStatus] = useState(ReadyRouteStatusEnum.DRAFT);
    const [guideType, setGuideType] = useState<ReadyRouteGuideTypeEnum | null>(null);
    const [mobility, setMobility] = useState<ReadyRouteMobilityEnum>(ReadyRouteMobilityEnum.WALK);
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [stopLang, setStopLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [locales, setLocales] = useState<Partial<Record<Languages, LocaleDraft>>>({});
    const [coverUrl, setCoverUrl] = useState<string | null>(null);
    const [authorPartnerId, setAuthorPartnerId] = useState<number | null>(null);
    const [authorName, setAuthorName] = useState('');
    const [authorAvatarUrl, setAuthorAvatarUrl] = useState<string | null>(null);
    const [durationFromMinutes, setDurationFromMinutes] = useState<number | ''>('');
    const [durationToMinutes, setDurationToMinutes] = useState<number | ''>('');
    const [distanceKm, setDistanceKm] = useState<number | ''>('');
    const [stops, setStops] = useState<DraftStop[]>([]);
    const [pool, setPool] = useState<PoolPlace[]>([]);
    const [detailsPlace, setDetailsPlace] = useState<{
        attractionId: number | null;
        establishmentId?: number | null;
    } | null>(null);
    const [center, setCenter] = useState({ lat: 52.52, lng: 13.405 });
    const [selectedPlaceKey, setSelectedPlaceKey] = useState<string | null>(null);
    const [selectedStopKey, setSelectedStopKey] = useState<string | null>(null);
    const [draggingStopKey, setDraggingStopKey] = useState<string | null>(null);
    const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
    const [textSourceTab, setTextSourceTab] = useState<'route' | 'attraction'>('route');
    const [mapSearch, setMapSearch] = useState('');
    const [geometry, setGeometry] = useState<[number, number][]>([]);
    const [routeTitle, setRouteTitle] = useState<string | null>(null);

    const markDirty = () => setDirty(true);

    const statusOptions = useMemo(
        () => enumOptions(Object.values(ReadyRouteStatusEnum), t, 'enums.readyRouteStatus'),
        [t],
    );
    const guideTypeOptions = useMemo(
        () => enumOptions(Object.values(ReadyRouteGuideTypeEnum), t, 'enums.readyRouteGuideType'),
        [t],
    );

    const reindex = (list: DraftStop[]) => list.map((stop, index) => ({ ...stop, order: index }));

    const contentLanguages = useMemo(() => contentLanguagesForCountry(country), [country]);

    useEffect(() => {
        if (!contentLanguages.includes(lang)) {
            setLang(contentLanguages[0] || CONTENT_BASE_LANGUAGE);
        }
        if (!contentLanguages.includes(stopLang)) {
            setStopLang(contentLanguages[0] || CONTENT_BASE_LANGUAGE);
        }
    }, [contentLanguages, lang, stopLang]);

    const loadPool = useCallback(async (id: number) => {
        const { data } = await api.cityReadyRoutesApi.mapPool({ cityId: id });

        setPool([
            ...(data?.attractions || []).map((a) => ({
                ...a,
                placeKey: `attraction:${a.attractionId}`,
            })),
            ...(data?.establishments || []).map((e) => ({
                ...e,
                attractionId: null,
                archived: e.status === 'closed',
                placeKey: `establishment:${e.establishmentId}`,
                reviewStatus:
                    e.status === 'published'
                        ? AttractionReviewStatusEnum.APPROVED
                        : e.status === 'closed'
                          ? AttractionReviewStatusEnum.REJECTED
                          : AttractionReviewStatusEnum.NEED_REVIEW,
            })),
        ]);
        setCityTitle(data?.city || '');
        setCountry(data?.country ?? null);
        if (data?.lat != null && data?.lng != null) {
            setCenter({ lat: data.lat, lng: data.lng });
        }
    }, []);

    const hydrate = useCallback((route: CityReadyRouteRO) => {
        setCityId(route.cityId);
        setCountry(route.country ?? null);
        setStatus(route.status);
        setGuideType(route.guideType ?? null);
        setMobility(route.mobility || ReadyRouteMobilityEnum.WALK);
        setCoverUrl(route.coverUrl);
        setAuthorName(route.authorName || '');
        setAuthorAvatarUrl(route.authorAvatarUrl);
        setAuthorPartnerId(route.authorId ?? null);
        setDurationFromMinutes(route.durationFromMinutes ?? '');
        setDurationToMinutes(route.durationToMinutes ?? '');
        setDistanceKm(route.distanceKm ?? '');
        setRouteTitle(route.title || null);
        setCityTitle(route.cityTitle || '');

        const nextLocales: Partial<Record<Languages, LocaleDraft>> = {};
        for (const row of route.locales || []) {
            nextLocales[row.language] = {
                title: row.title || '',
                teaser: row.teaser || '',
                description: row.description || '',
                transitNote: row.transitNote || '',
            };
        }
        if (
            !nextLocales[CONTENT_BASE_LANGUAGE] &&
            (route.title || route.teaser || route.description)
        ) {
            nextLocales[CONTENT_BASE_LANGUAGE] = {
                title: route.title || '',
                teaser: route.teaser || '',
                description: route.description || '',
                transitNote: '',
            };
        }
        setLocales(nextLocales);
        setLang(CONTENT_BASE_LANGUAGE);

        setStops(
            [...(route.stops || [])]
                .sort((a, b) => a.order - b.order)
                .map((stop) => {
                    const stopLocales: StopContentLocales = {};
                    const stopAudio: StopAudioLocales = {};

                    for (const row of stop.locales || []) {
                        stopLocales[row.language] = {
                            hook: row.content?.hook || '',
                            guideTalk: row.content?.guideTalk || '',
                        };
                        stopAudio[row.language] = {
                            guideAudioUrl: row.guideAudioUrl ?? null,
                            guideAudioDurationSec: row.guideAudioDurationSec ?? null,
                        };
                    }

                    return {
                        key: nextStopKey(),
                        id: stop.id,
                        order: stop.order,
                        kind: stop.kind,
                        attractionId: stop.attractionId,
                        establishmentId: stop.establishmentId ?? null,
                        fieldExcluded: stop.fieldExcluded === true,
                        optionalBreak: stop.optionalBreak === true,
                        breakMinutes: stop.breakMinutes ?? null,
                        lat: stop.lat,
                        lng: stop.lng,
                        alternateStart: stop.alternateStart === true,
                        routeRoleOverride: stop.routeRoleOverride ?? null,
                        title: stop.establishmentTitle || stop.attractionTitle || undefined,
                        reviewStatus: stop.attractionReviewStatus || undefined,
                        locales: stopLocales,
                        audio: stopAudio,
                    };
                }),
        );
        setStopLang(CONTENT_BASE_LANGUAGE);
        setDirty(false);
    }, []);

    useEffect(() => {
        if (!opened) return;

        setTab(textOnly ? 'constructor' : 'meta');
        setDirty(false);
        setDetailsPlace(null);
        setSelectedPlaceKey(null);
        setSelectedStopKey(null);
        setMapSearch('');
        setGeometry([]);

        if (isCreate) {
            setLoading(false);
            setCityId(initialCityId ?? null);
            setCountry(null);
            setStatus(ReadyRouteStatusEnum.DRAFT);
            setGuideType(null);
            setMobility(ReadyRouteMobilityEnum.WALK);
            setLang(CONTENT_BASE_LANGUAGE);
            setStopLang(CONTENT_BASE_LANGUAGE);
            setLocales({});
            setCoverUrl(null);
            setAuthorPartnerId(null);
            setAuthorName('');
            setAuthorAvatarUrl(null);
            setDurationFromMinutes('');
            setDurationToMinutes('');
            setDistanceKm('');
            setStops([]);
            setPool([]);
            setRouteTitle(null);
            setCityTitle('');

            if (initialCityId) {
                loadPool(initialCityId).catch(() => undefined);
            }

            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityReadyRoutesApi.getOne(routeId!);

                if (cancelled || !data) return;
                hydrate(data);
                await loadPool(data.cityId);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                onClose();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [opened, routeId, isCreate, textOnly, initialCityId, hydrate, loadPool, onClose]);

    const pathPoints = useMemo(
        () =>
            stops
                .filter((s) => !s.optionalBreak && s.lat != null && s.lng != null)
                .map((s) => ({ lat: s.lat!, lng: s.lng! })),
        [stops],
    );

    useEffect(() => {
        if (pathPoints.length < 2) {
            setGeometry([]);

            return;
        }

        const timer = window.setTimeout(async () => {
            try {
                const { data } = await api.cityReadyRoutesApi.estimatePath({
                    data: { points: pathPoints },
                });

                setGeometry(data?.geometry || []);
                if (durationFromMinutes === '' && data?.durationFromMinutes != null) {
                    setDurationFromMinutes(data.durationFromMinutes);
                }
                if (durationToMinutes === '' && data?.durationToMinutes != null) {
                    setDurationToMinutes(data.durationToMinutes);
                }
                if (distanceKm === '' && data?.distanceKm != null) {
                    setDistanceKm(data.distanceKm);
                }
            } catch {
                // estimate is best-effort
            }
        }, 400);

        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathPoints]);

    const publishWarnings = useMemo(() => {
        const issues: string[] = [];

        for (const stop of stops) {
            if (stop.kind === ReadyRouteStopKindEnum.WAYPOINT) continue;
            if (stop.lat == null || stop.lng == null) {
                issues.push(
                    t('editors.route.noCoordinates', {
                        title: stop.title || stop.attractionId,
                    }),
                );
            }
            if (stop.reviewStatus && stop.reviewStatus !== AttractionReviewStatusEnum.APPROVED) {
                issues.push(`${stop.title || stop.attractionId}: ${stop.reviewStatus}`);
            }
        }

        return issues;
    }, [stops, t]);

    const selectedAttraction = useMemo(
        () => pool.find((a) => a.placeKey === selectedPlaceKey) || null,
        [pool, selectedPlaceKey],
    );

    const selectedStop = useMemo(
        () => stops.find((s) => s.key === selectedStopKey) || null,
        [stops, selectedStopKey],
    );

    const primaryStartKey =
        stops.find((stop) => stop.kind !== ReadyRouteStopKindEnum.WAYPOINT && !stop.optionalBreak)
            ?.key ?? null;

    const selectedStopPool = useMemo(() => {
        if (!selectedStop) return null;

        return pool.find((a) => a.placeKey === placeKeyOf(selectedStop)) || null;
    }, [pool, selectedStop]);

    const selectAttraction = (placeKey: string) => {
        setSelectedPlaceKey(placeKey);
        setSelectedStopKey(null);
        setTextSourceTab('attraction');
    };

    const selectStop = (stopKey: string) => {
        setSelectedStopKey(stopKey);
        setSelectedPlaceKey(null);
        setTextSourceTab('route');
    };

    const patchStopContent = (
        key: string,
        language: Languages,
        field: keyof ReadyRouteStopContentOverride,
        value: string,
    ) => {
        setStops((prev) =>
            prev.map((stop) =>
                stop.key === key
                    ? {
                          ...stop,
                          locales: {
                              ...stop.locales,
                              [language]: {
                                  ...(stop.locales[language] || emptyStopContent()),
                                  [field]: value,
                              },
                          },
                      }
                    : stop,
            ),
        );
        markDirty();
    };

    const patchStopAudio = async (key: string, language: Languages, url: string | null) => {
        const durationSec = url ? await probeAudioDurationSec(url) : null;

        setStops((prev) =>
            prev.map((stop) =>
                stop.key === key
                    ? {
                          ...stop,
                          audio: {
                              ...stop.audio,
                              [language]: {
                                  ...(stop.audio[language] || emptyStopAudio()),
                                  guideAudioUrl: url,
                                  guideAudioDurationSec: durationSec,
                              },
                          },
                      }
                    : stop,
            ),
        );
        markDirty();
    };

    const addAttractionStop = (attraction: PoolPlace) => {
        if (attraction.lat == null || attraction.lng == null) {
            notifications.show({
                color: 'red',
                message: t('editors.route.noCoordinatesToast'),
            });

            return;
        }

        if (stops.some((s) => placeKeyOf(s) === attraction.placeKey)) {
            notifications.show({
                color: 'red',
                message: t('editors.route.alreadyInRoute'),
            });

            return;
        }

        const key = nextStopKey();

        setStops((prev) =>
            reindex([
                ...prev,
                {
                    key,
                    order: prev.length,
                    kind:
                        attraction.establishmentId != null
                            ? ReadyRouteStopKindEnum.ESTABLISHMENT
                            : ReadyRouteStopKindEnum.ATTRACTION,
                    attractionId: attraction.attractionId,
                    establishmentId: attraction.establishmentId ?? null,
                    optionalBreak: false,
                    lat: attraction.lat,
                    lng: attraction.lng,
                    alternateStart: false,
                    routeRoleOverride: null,
                    title: attraction.title,
                    reviewStatus: attraction.reviewStatus,
                    locales: {},
                    audio: {},
                },
            ]),
        );
        selectStop(key);
        markDirty();
    };

    const onAddWaypoint = (lat: number, lng: number) => {
        const key = nextStopKey();
        setStops((prev) =>
            reindex([
                ...prev,
                {
                    key,
                    order: prev.length,
                    kind: ReadyRouteStopKindEnum.WAYPOINT,
                    attractionId: null,
                    lat,
                    lng,
                    alternateStart: false,
                    routeRoleOverride: null,
                    title: t('editors.route.constructorTab.waypoint'),
                    locales: {},
                    audio: {},
                },
            ]),
        );
        selectStop(key);
        markDirty();
    };

    const reorderStop = (fromKey: string, toKey: string) => {
        if (fromKey === toKey) return;

        setStops((prev) => {
            const from = prev.findIndex((s) => s.key === fromKey);
            const to = prev.findIndex((s) => s.key === toKey);

            if (from < 0 || to < 0) return prev;

            const next = [...prev];
            const [item] = next.splice(from, 1);

            next.splice(to, 0, item);
            markDirty();

            return reindex(next);
        });
    };

    const onMovePoint = async (kind: 'stop' | 'place', key: string, lat: number, lng: number) => {
        if (textOnly || saving) return;
        const point =
            kind === 'stop'
                ? stops.find((s) => s.key === key)
                : pool.find((p) => p.placeKey === key);
        if (!point || point.lat == null || point.lng == null) return;
        if (Math.abs(point.lat - lat) < 0.0000001 && Math.abs(point.lng - lng) < 0.0000001) return;
        const stop = kind === 'stop' ? (point as DraftStop) : null;
        const place = kind === 'place' ? (point as PoolPlace) : null;
        const persisted = routeId != null && (!stop || stop.id != null);
        const scope = stop ? 'остановки только этого маршрута' : 'места во всём каталоге';
        const message = persisted
            ? 'Сохранить новые координаты ' + scope + '?'
            : 'Эта остановка ещё не сохранена. Изменить её координаты в черновике? Они сохранятся вместе с маршрутом.';
        if (
            !window.confirm(
                (point.title || 'Точка') +
                    '\n' +
                    message +
                    '\n' +
                    lat.toFixed(6) +
                    ', ' +
                    lng.toFixed(6),
            )
        )
            return;
        setSaving(true);
        try {
            if (persisted) {
                await api.cityReadyRoutesApi.moveMapPoint({
                    routeId: routeId!,
                    data: {
                        kind: stop
                            ? 'stop'
                            : place!.establishmentId != null
                              ? 'establishment'
                              : 'attraction',
                        id: stop ? stop.id! : (place!.establishmentId ?? place!.attractionId)!,
                        lat,
                        lng,
                        beforeLat: point.lat,
                        beforeLng: point.lng,
                    },
                });
            } else if (place) {
                notifications.show({
                    color: 'orange',
                    message: 'Сначала сохраните новый маршрут, затем переместите место.',
                });
                return;
            }
            if (stop) {
                setStops((prev) => prev.map((s) => (s.key === key ? { ...s, lat, lng } : s)));
                if (!persisted) markDirty();
            } else {
                setPool((prev) => prev.map((p) => (p.placeKey === key ? { ...p, lat, lng } : p)));
            }
            notifications.show({
                color: 'green',
                message: persisted ? 'Координаты сохранены' : 'Координаты изменены в черновике',
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const removeStop = (key: string) => {
        setStops((prev) => reindex(prev.filter((s) => s.key !== key)));
        if (selectedStopKey === key) setSelectedStopKey(null);
        markDirty();
    };

    const activeLocale = locales[lang] || emptyLocale();

    const patchLocale = (field: keyof LocaleDraft, value: string) => {
        setLocales((prev) => ({
            ...prev,
            [lang]: { ...(prev[lang] || emptyLocale()), [field]: value },
        }));
        markDirty();
    };

    const localeChips = contentLanguages.map((language) => {
        const draft = locales[language];
        const filled = !!(
            draft?.title?.trim() ||
            draft?.teaser?.trim() ||
            draft?.description?.trim()
        );

        return {
            value: language,
            label: language.toUpperCase(),
            mark: filled ? ('filled' as const) : ('empty' as const),
        };
    });

    const stopLocaleChips = contentLanguages.map((language) => {
        const draft = selectedStop?.locales[language];
        const filled = hasStopOverride(draft);
        const attractionFilled = !!(
            poolLocale(selectedStopPool || selectedAttraction, language)?.hook ||
            poolLocale(selectedStopPool || selectedAttraction, language)?.guideTalk
        );

        return {
            value: language,
            label: language.toUpperCase(),
            mark: filled
                ? ('filled' as const)
                : attractionFilled
                  ? ('reviewed' as const)
                  : ('empty' as const),
        };
    });

    const save = async () => {
        if (textOnly && routeId != null) {
            setSaving(true);

            try {
                const { data } = await api.cityReadyRoutesApi.update({
                    id: routeId,
                    data: {
                        stops: stops
                            .filter(
                                (stop) =>
                                    stop.id != null &&
                                    stop.kind !== ReadyRouteStopKindEnum.WAYPOINT,
                            )
                            .map((stop) => ({
                                id: stop.id,
                                order: stop.order,
                                kind: stop.kind,
                                attractionId: stop.attractionId,
                                establishmentId: stop.establishmentId ?? null,
                                fieldExcluded: stop.fieldExcluded === true,
                                optionalBreak: stop.optionalBreak === true,
                                breakMinutes: stop.breakMinutes ?? null,
                                locales: (Object.keys(stop.locales) as Languages[]).map(
                                    (language) => {
                                        const content = stop.locales[language];

                                        return {
                                            language,
                                            content: {
                                                hook: content?.hook?.trim() || null,
                                                guideTalk: content?.guideTalk?.trim() || null,
                                            },
                                        };
                                    },
                                ),
                            })),
                    },
                });

                if (data) hydrate(data);
                notifications.show({
                    color: 'green',
                    message: t('editors.route.routeSaved'),
                });
                setDirty(false);
                onSaved();
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                setSaving(false);
            }

            return;
        }

        if (!cityId) {
            notifications.show({
                color: 'red',
                message: t('editors.route.cityRequired'),
            });

            return;
        }

        const baseTitle = (locales[CONTENT_BASE_LANGUAGE]?.title || '').trim();
        if (!baseTitle) {
            notifications.show({
                color: 'red',
                message: t('editors.route.titleRequiredIn', {
                    lang: CONTENT_BASE_LANGUAGE.toUpperCase(),
                }),
            });

            return;
        }

        if (status === ReadyRouteStatusEnum.PUBLISHED && publishWarnings.length) {
            const ok = window.confirm(
                t('editors.route.publishWithWarnings', {
                    warnings:
                        publishWarnings.slice(0, 8).join('\n') +
                        (publishWarnings.length > 8 ? '\n…' : ''),
                }),
            );

            if (!ok) return;
        }

        setSaving(true);

        const localePayload = contentLanguages
            .map((language) => {
                const draft = locales[language] || emptyLocale();
                const title = draft.title.trim();
                if (!title) return null;

                return {
                    language,
                    title,
                    teaser: draft.teaser.trim() || null,
                    description: draft.description.trim() || null,
                    transitNote: draft.transitNote.trim() || null,
                };
            })
            .filter((row): row is NonNullable<typeof row> => !!row);

        const payload = {
            cityId,
            status,
            guideType,
            mobility,
            coverUrl,
            authorId: authorPartnerId,
            durationFromMinutes: durationFromMinutes === '' ? null : Number(durationFromMinutes),
            durationToMinutes: durationToMinutes === '' ? null : Number(durationToMinutes),
            distanceKm: distanceKm === '' ? null : Number(distanceKm),
            locales: localePayload,
            stops: stops.map((stop) => {
                const stopLocales =
                    stop.kind !== ReadyRouteStopKindEnum.WAYPOINT
                        ? contentLanguages
                              .map((language) => {
                                  const content = stop.locales[language];
                                  const audio = stop.audio[language];

                                  // Локаль без текста, но с записанной озвучкой всё равно
                                  // должна уехать: иначе сохранение маршрута её потеряет.
                                  if (!hasStopOverride(content) && !hasStopAudio(audio)) {
                                      return null;
                                  }

                                  return {
                                      language,
                                      content: {
                                          hook: content?.hook?.trim() || null,
                                          guideTalk: content?.guideTalk?.trim() || null,
                                      },
                                      guideAudioUrl: audio?.guideAudioUrl ?? null,
                                      guideAudioDurationSec: audio?.guideAudioDurationSec ?? null,
                                  };
                              })
                              .filter((row): row is NonNullable<typeof row> => !!row)
                        : [];

                return {
                    id: stop.id,
                    order: stop.order,
                    kind: stop.kind,
                    attractionId: stop.attractionId,
                    establishmentId: stop.establishmentId ?? null,
                    fieldExcluded: stop.fieldExcluded === true,
                    optionalBreak: stop.optionalBreak === true,
                    breakMinutes: stop.breakMinutes ?? null,
                    lat: stop.lat,
                    lng: stop.lng,
                    alternateStart: stop.alternateStart === true,
                    routeRoleOverride:
                        stop.kind === ReadyRouteStopKindEnum.WAYPOINT
                            ? null
                            : stop.routeRoleOverride,
                    locales: stopLocales,
                };
            }),
        };

        try {
            if (isCreate) {
                const { data } = await api.cityReadyRoutesApi.create({ data: payload });

                notifications.show({
                    color: 'green',
                    message: t('editors.route.routeCreated'),
                });
                onSaved();
                if (data) onCreated?.(data.id);
                else onClose();
            } else {
                const { data } = await api.cityReadyRoutesApi.update({
                    id: routeId!,
                    data: payload,
                });
                if (data) hydrate(data);
                notifications.show({
                    color: 'green',
                    message: t('editors.route.routeSaved'),
                });
                setDirty(false);
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const stopDisplayTitle = (stop: DraftStop) =>
        stop.kind === ReadyRouteStopKindEnum.WAYPOINT
            ? t('editors.route.constructorTab.waypoint')
            : attractionTitle(pool.find((p) => p.placeKey === placeKeyOf(stop)), Languages.RU, stop.title) || `#${stop.attractionId ?? stop.establishmentId}`;

    const mapStops = stops
        .filter((s) => s.lat != null && s.lng != null)
        .map((s) => ({
            key: s.key,
            order: s.order,
            kind:
                s.kind === ReadyRouteStopKindEnum.WAYPOINT
                    ? ('waypoint' as const)
                    : ('attraction' as const),
            placeKey: placeKeyOf(s),
            optionalBreak: s.optionalBreak,
            fieldExcluded: s.fieldExcluded,
            lat: s.lat!,
            lng: s.lng!,
            title: stopDisplayTitle(s),
        }));

    const mapPool = useMemo(
        () =>
            pool.filter(
                (attraction) =>
                    !attraction.archived &&
                    (mobility !== ReadyRouteMobilityEnum.WALK ||
                        attraction.establishmentId != null ||
                        stops.some((stop) => placeKeyOf(stop) === attraction.placeKey) ||
                        !stops.some((stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) ||
                        (attraction.lat != null && attraction.lng != null &&
                            stops.some((stop) =>
                                stop.lat != null && stop.lng != null &&
                                haversineKm({ lat: attraction.lat!, lng: attraction.lng! }, { lat: stop.lat, lng: stop.lng }) <= 1))) &&
                    attraction.reviewStatus !== AttractionReviewStatusEnum.REJECTED &&
                    attraction.reviewStatus !== AttractionReviewStatusEnum.REJECTED_MCP,
            ),
        [pool, stops, mobility],
    );

    const mapSearchHits = useMemo(() => {
        const query = normalizeSearch(mapSearch);

        if (!query) {
            return {
                active: false,
                placeKeys: [] as string[],
                stopKeys: [] as string[],
                count: 0,
            };
        }

        const placeKeys = new Set<string>();
        const stopKeys = new Set<string>();

        for (const attraction of mapPool) {
            if (attractionSearchBlob(attraction).includes(query)) {
                placeKeys.add(attraction.placeKey);
            }
        }

        for (const stop of stops) {
            if (stop.kind === ReadyRouteStopKindEnum.WAYPOINT) {
                const blob = normalizeSearch(`waypoint w${stop.order + 1} ${stop.order + 1}`);

                if (blob.includes(query)) stopKeys.add(stop.key);
                continue;
            }

            const titleHit = stop.title ? normalizeSearch(stop.title).includes(query) : false;
            const attractionHit = placeKeys.has(placeKeyOf(stop));

            if (titleHit || attractionHit) stopKeys.add(stop.key);
        }

        // Count unique map markers: pool hits not already on route + matching stops
        const onRouteAttractionIds = new Set(
            stops.filter((s) => s.kind !== ReadyRouteStopKindEnum.WAYPOINT).map(placeKeyOf),
        );
        const poolOnlyHits = [...placeKeys].filter((id) => !onRouteAttractionIds.has(id));
        const count = poolOnlyHits.length + stopKeys.size;

        return {
            active: true,
            placeKeys: [...placeKeys],
            stopKeys: [...stopKeys],
            count,
        };
    }, [mapSearch, mapPool, stops]);

    const activeTab = textOnly && tab !== 'history' ? 'constructor' : tab;
    const editorTabs = (textOnly
        ? [
              {
                  value: 'constructor',
                  label: t('editors.route.tabs.constructor'),
                  dirty: activeTab === 'constructor' && dirty,
              },
          ]
        : [
              {
                  value: 'meta',
                  label: t('editors.route.tabs.meta'),
                  dirty: activeTab === 'meta' && dirty,
              },
              {
                  value: 'locales',
                  label: t('editors.route.tabs.locales'),
                  dirty: activeTab === 'locales' && dirty,
              },
              {
                  value: 'constructor',
                  label: t('editors.route.tabs.constructor'),
                  dirty: activeTab === 'constructor' && dirty,
              },
          ]).concat([{ value: 'history', label: 'История', dirty: false }]);

    return (
        <EntityEditorShell
            suspended={detailsPlace != null}
            opened={opened}
            onClose={onClose}
            loading={loading}
            title={
                isCreate
                    ? t('editors.route.newTitle')
                    : routeTitle || t('editors.route.fallbackTitle', { id: routeId })
            }
            subtitle={[
                cityTitle,
                `Точек маршрута: ${stops.length}`,
                `Протяжённость: ${distanceKm === '' ? '—' : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(distanceKm)} км`}`,
                `Кафе: ${stops.filter((stop) => stop.establishmentId != null).length}`,
            ].filter(Boolean).join(' · ')}
            status={{
                label: enumLabel(t, 'enums.readyRouteStatus', status),
                color: status === ReadyRouteStatusEnum.PUBLISHED ? 'green' : 'orange',
            }}
            dirty={dirty}
            tabs={editorTabs}
            activeTab={activeTab}
            onTabChange={setTab}
            size="95%"
            footer={
                <EditorFooter
                    onCancel={onClose}
                    onSave={save}
                    saving={saving}
                    saveLabel={isCreate ? t('common.create') : t('editors.route.saveRoute')}
                    left={
                        <Group gap="sm">
                            {!isCreate && !textOnly && (
                                <Button
                                    color="red"
                                    variant="light"
                                    loading={deleting}
                                    onClick={async () => {
                                        if (routeId == null) return;
                                        if (
                                            !window.confirm(
                                                t('editors.route.deleteConfirm', {
                                                    id: routeId,
                                                }),
                                            )
                                        ) {
                                            return;
                                        }

                                        setDeleting(true);

                                        try {
                                            await api.cityReadyRoutesApi.remove(routeId);
                                            notifications.show({
                                                color: 'green',
                                                message: t('editors.route.deleted'),
                                            });
                                            onDeleted?.();
                                            onSaved();
                                            onClose();
                                        } catch (error) {
                                            notifications.show({
                                                color: 'red',
                                                message: apiErrorMessage(error),
                                            });
                                        } finally {
                                            setDeleting(false);
                                        }
                                    }}
                                >
                                    {t('common.delete')}
                                </Button>
                            )}
                            {!textOnly && publishWarnings.length > 0 ? (
                                <Text size="xs" c="orange">
                                    {t('editors.route.publishWarningCount', {
                                        count: publishWarnings.length,
                                    })}
                                </Text>
                            ) : null}
                        </Group>
                    }
                />
            }
        >
            {activeTab === 'meta' && (
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(260px, 0.9fr) minmax(340px, 1.2fr)',
                        gap: 28,
                        alignItems: 'start',
                    }}
                >
                    <Section
                        boxed
                        title={t('editors.route.cover.title')}
                        description={t('editors.route.cover.description')}
                    >
                        <MediaDropzone
                            label=""
                            kind="image"
                            imageType={ImageTypeEnum.CITY_READY_ROUTE}
                            allowUrl={false}
                            value={coverUrl}
                            onChange={(url) => {
                                setCoverUrl(url);
                                markDirty();
                            }}
                        />
                    </Section>

                    <Stack gap="lg">
                        <Section boxed title={t('editors.route.placement.title')}>
                            <Group grow align="flex-start">
                                <CitySearchSelect
                                    label={t('editors.route.placement.city')}
                                    value={cityId}
                                    initialLabel={cityTitle || undefined}
                                    onChange={(id) => {
                                        setCityId(id);
                                        if (!id) setCountry(null);
                                        markDirty();
                                        if (id) loadPool(id).catch(() => undefined);
                                    }}
                                />
                                <Select
                                    label={t('editors.route.placement.status')}
                                    data={statusOptions}
                                    value={status}
                                    onChange={(v) => {
                                        setStatus(
                                            (v as ReadyRouteStatusEnum) ||
                                                ReadyRouteStatusEnum.DRAFT,
                                        );
                                        markDirty();
                                    }}
                                />
                            </Group>
                            <Select
                                label={t('editors.route.placement.guideType')}
                                description={t('editors.route.placement.guideTypeHint')}
                                data={guideTypeOptions}
                                clearable
                                value={guideType}
                                onChange={(v) => {
                                    setGuideType((v as ReadyRouteGuideTypeEnum) || null);
                                    markDirty();
                                }}
                            />
                            <Select
                                label={t('editors.route.placement.mobility')}
                                description={t('editors.route.placement.mobilityHint')}
                                data={[
                                    {
                                        value: ReadyRouteMobilityEnum.WALK,
                                        label: t('editors.route.placement.mobilityWalk'),
                                    },
                                    {
                                        value: ReadyRouteMobilityEnum.TRANSIT,
                                        label: t('editors.route.placement.mobilityTransit'),
                                    },
                                ]}
                                value={mobility}
                                onChange={(v) => {
                                    setMobility(
                                        (v as ReadyRouteMobilityEnum) ||
                                            ReadyRouteMobilityEnum.WALK,
                                    );
                                    markDirty();
                                }}
                            />
                        </Section>

                        <Section
                            boxed
                            title={t('editors.route.author.title')}
                            description={t('editors.route.author.description')}
                        >
                            <AuthorSelect
                                label=""
                                value={authorPartnerId}
                                fallbackName={authorName}
                                onChange={(author) => {
                                    setAuthorPartnerId(author?.id ?? null);
                                    setAuthorName(author?.name || '');
                                    setAuthorAvatarUrl(author?.avatarUrl || null);
                                    markDirty();
                                }}
                            />
                            {(authorName || authorAvatarUrl) && (
                                <Group gap="sm" wrap="nowrap">
                                    {authorAvatarUrl ? (
                                        <Box
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                background: 'var(--mantine-color-gray-1)',
                                            }}
                                        >
                                            <img
                                                src={authorAvatarUrl}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    ) : null}
                                    <Text size="sm" c="dimmed">
                                        {t('editors.route.author.attribution', {
                                            name: authorName || '—',
                                        })}
                                    </Text>
                                </Group>
                            )}
                        </Section>

                        <Section
                            boxed
                            title={t('editors.route.walkingStats.title')}
                            description={t('editors.route.walkingStats.description')}
                        >
                            <Group grow align="flex-start">
                                <NumberInput
                                    label={t('editors.route.walkingStats.from')}
                                    min={0}
                                    value={durationFromMinutes}
                                    onChange={(v) => {
                                        setDurationFromMinutes(typeof v === 'number' ? v : '');
                                        markDirty();
                                    }}
                                />
                                <NumberInput
                                    label={t('editors.route.walkingStats.to')}
                                    min={0}
                                    value={durationToMinutes}
                                    onChange={(v) => {
                                        setDurationToMinutes(typeof v === 'number' ? v : '');
                                        markDirty();
                                    }}
                                />
                                <NumberInput
                                    label={t('editors.route.walkingStats.distance')}
                                    min={0}
                                    decimalScale={2}
                                    value={distanceKm}
                                    onChange={(v) => {
                                        setDistanceKm(typeof v === 'number' ? v : '');
                                        markDirty();
                                    }}
                                />
                            </Group>
                        </Section>
                    </Stack>
                </Box>
            )}

            {activeTab === 'locales' && (
                <Stack gap="md">
                    <Section
                        boxed
                        title={t('editors.route.catalogCopy.title')}
                        description={t('editors.route.catalogCopy.description')}
                    >
                        <LanguageChips
                            languages={localeChips}
                            value={lang}
                            onChange={(v) => setLang(v as Languages)}
                        />
                        <TextInput
                            label={t('editors.route.catalogCopy.titleField', {
                                lang: lang.toUpperCase(),
                            })}
                            required={lang === CONTENT_BASE_LANGUAGE}
                            value={activeLocale.title}
                            onChange={(e) => patchLocale('title', e.currentTarget.value)}
                        />
                        <Textarea
                            label={t('editors.route.catalogCopy.teaser', {
                                lang: lang.toUpperCase(),
                            })}
                            minRows={2}
                            value={activeLocale.teaser}
                            onChange={(e) => patchLocale('teaser', e.currentTarget.value)}
                        />
                        <Textarea
                            label={t('editors.route.catalogCopy.description2', {
                                lang: lang.toUpperCase(),
                            })}
                            minRows={4}
                            value={activeLocale.description}
                            onChange={(e) => patchLocale('description', e.currentTarget.value)}
                        />
                        {mobility === ReadyRouteMobilityEnum.TRANSIT && (
                            <Textarea
                                label={t('editors.route.catalogCopy.transitNote', {
                                    lang: lang.toUpperCase(),
                                })}
                                description={t('editors.route.catalogCopy.transitNoteHint')}
                                minRows={2}
                                value={activeLocale.transitNote}
                                onChange={(e) => patchLocale('transitNote', e.currentTarget.value)}
                            />
                        )}
                    </Section>
                </Stack>
            )}

            {activeTab === 'constructor' && (
                <Stack gap="md">
                    {!cityId ? (
                        <Text c="dimmed">{t('editors.route.constructorTab.selectCityHint')}</Text>
                    ) : (
                        <Box
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'minmax(300px, 1.2fr) 300px minmax(340px, 1.3fr)',
                                gap: 16,
                                minHeight: 520,
                                alignItems: 'stretch',
                            }}
                        >
                            <Stack gap="sm" style={{ minWidth: 0 }}>
                                <Group justify="space-between" wrap="nowrap" gap="sm">
                                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                        <TextInput
                                            size="xs"
                                            placeholder={t(
                                                'editors.route.constructorTab.searchPlaceholder',
                                            )}
                                            value={mapSearch}
                                            onChange={(e) => setMapSearch(e.currentTarget.value)}
                                            leftSection={<IconSearch size={14} />}
                                            rightSection={
                                                mapSearch ? (
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        aria-label={t('common.clearSearch')}
                                                        onClick={() => setMapSearch('')}
                                                    >
                                                        <IconX size={14} />
                                                    </ActionIcon>
                                                ) : null
                                            }
                                            style={{ flex: 1, maxWidth: 320 }}
                                        />
                                        {mapSearchHits.active && (
                                            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                                                {mapSearchHits.count
                                                    ? t('editors.route.constructorTab.found', {
                                                          count: mapSearchHits.count,
                                                      })
                                                    : t('editors.route.constructorTab.none')}
                                            </Text>
                                        )}
                                    </Group>
                                </Group>
                                {mapSearch && (
                                    <Group gap="xs">
                                        {mapPool
                                            .filter((p) =>
                                                attractionSearchBlob(p).includes(
                                                    normalizeSearch(mapSearch),
                                                ),
                                            )
                                            .slice(0, 12)
                                            .map((p) => (
                                                <Button
                                                    key={p.placeKey}
                                                    variant="light"
                                                    size="xs"
                                                    onClick={() => selectAttraction(p.placeKey)}
                                                >
                                                    {p.establishmentId ? '☕ ' : ''}
                                                    {p.title}
                                                </Button>
                                            ))}
                                    </Group>
                                )}
                                <ConstructorMap
                                    center={center}
                                    attractions={mapPool.map((a) => ({
                                        placeKey: a.placeKey,
                                        establishment: a.establishmentId != null,
                                        title: attractionTitle(a, Languages.RU) || a.title,
                                        lat: a.lat,
                                        lng: a.lng,
                                        reviewStatus: a.reviewStatus,
                                        archived: a.archived,
                                    }))}
                                    stops={mapStops}
                                    geometry={geometry}
                                    selectedPlaceKey={selectedPlaceKey}
                                    selectedStopKey={selectedStopKey}
                                    searchActive={mapSearchHits.active}
                                    highlightedPlaceKeys={mapSearchHits.placeKeys}
                                    highlightedStopKeys={mapSearchHits.stopKeys}
                                    readOnly={textOnly || saving}
                                    onSelectPlace={selectAttraction}
                                    onSelectStop={selectStop}
                                    onAddWaypoint={onAddWaypoint}
                                    onClearSelection={() => {
                                        setSelectedStopKey(null);
                                        setSelectedPlaceKey(null);
                                    }}
                                    onMovePoint={onMovePoint}
                                />
                            </Stack>

                            <Box
                                style={{
                                    width: 300,
                                    maxHeight: '70vh',
                                    overflow: 'auto',
                                    minWidth: 0,
                                }}
                            >
                                <Section
                                    title={t('editors.route.constructorTab.stopsTitle')}
                                    description={
                                        textOnly
                                            ? t('editors.route.constructorTab.textOnlyHint')
                                            : t('editors.route.constructorTab.stopsDescription')
                                    }
                                >
                                    <Stack gap={6}>
                                        {stops.map((stop) => {
                                            const active = stop.key === selectedStopKey;
                                            const dragging = draggingStopKey === stop.key;
                                            const dropTarget = dropTargetKey === stop.key;
                                            const title = stopDisplayTitle(stop);

                                            return (
                                                <Box
                                                    key={stop.key}
                                                    draggable={!textOnly}
                                                    onDragStart={(e) => {
                                                        if (textOnly) return;
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        e.dataTransfer.setData(
                                                            'text/plain',
                                                            stop.key,
                                                        );
                                                        setDraggingStopKey(stop.key);
                                                    }}
                                                    onDragEnd={() => {
                                                        setDraggingStopKey(null);
                                                        setDropTargetKey(null);
                                                    }}
                                                    onDragOver={(e) => {
                                                        if (textOnly) return;
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                        if (dropTargetKey !== stop.key) {
                                                            setDropTargetKey(stop.key);
                                                        }
                                                    }}
                                                    onDragLeave={() => {
                                                        if (dropTargetKey === stop.key) {
                                                            setDropTargetKey(null);
                                                        }
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        if (textOnly) return;
                                                        const fromKey =
                                                            e.dataTransfer.getData('text/plain') ||
                                                            draggingStopKey;

                                                        if (fromKey) {
                                                            reorderStop(fromKey, stop.key);
                                                        }
                                                        setDraggingStopKey(null);
                                                        setDropTargetKey(null);
                                                    }}
                                                    onClick={() => selectStop(stop.key)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '8px 10px',
                                                        border: dropTarget
                                                            ? '1px dashed var(--mantine-color-blue-5)'
                                                            : active
                                                              ? '1px solid var(--mantine-color-blue-5)'
                                                              : '1px solid var(--mantine-color-gray-3)',
                                                        borderRadius: 'var(--mantine-radius-sm)',
                                                        background: active
                                                            ? 'var(--mantine-color-blue-0)'
                                                            : 'var(--mantine-color-body)',
                                                        opacity: dragging ? 0.45 : 1,
                                                        cursor: textOnly ? 'pointer' : 'grab',
                                                    }}
                                                >
                                                    {!textOnly && (
                                                        <IconGripVertical
                                                            size={16}
                                                            color="var(--mantine-color-gray-5)"
                                                            style={{ flex: '0 0 auto' }}
                                                        />
                                                    )}
                                                    <Box
                                                        style={{
                                                            flex: '1 1 auto',
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Group gap={6} wrap="nowrap">
                                                            <Text
                                                                size="sm"
                                                                fw={500}
                                                                truncate
                                                                style={{ minWidth: 0 }}
                                                            >
                                                                {stop.optionalBreak
                                                                    ? '☕'
                                                                    : `${stop.order + 1}.`}{' '}
                                                                {title}
                                                            </Text>
                                                            {stop.fieldExcluded && (
                                                                <Tooltip
                                                                    label="Исключено при полевой проверке"
                                                                    withinPortal
                                                                    zIndex={400}
                                                                >
                                                                    <IconAlertTriangleFilled
                                                                        size={18}
                                                                        color="var(--mantine-color-yellow-6)"
                                                                        aria-label="Исключено при полевой проверке"
                                                                        style={{
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Group>
                                                        <Text size="xs" c="dimmed" truncate>
                                                            {stop.kind}
                                                            {stop.alternateStart &&
                                                            stop.key !== primaryStartKey
                                                                ? ` · ${t(
                                                                      'editors.route.constructorTab.alternateStartBadge',
                                                                  )}`
                                                                : ''}
                                                            {stopHasAnyOverride(stop.locales)
                                                                ? ` · ${t(
                                                                      'editors.route.constructorTab.override',
                                                                  )}`
                                                                : ''}
                                                        </Text>
                                                    </Box>
                                                    {!textOnly && (
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            color="red"
                                                            style={{ flex: '0 0 auto' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeStop(stop.key);
                                                            }}
                                                        >
                                                            <IconTrash size={14} />
                                                        </ActionIcon>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        {!stops.length && (
                                            <Text size="sm" c="dimmed">
                                                {t('editors.route.constructorTab.noStops')}
                                            </Text>
                                        )}
                                    </Stack>
                                </Section>
                            </Box>

                            <Box
                                style={{
                                    minWidth: 0,
                                    maxHeight: '70vh',
                                    overflow: 'auto',
                                }}
                            >
                                {selectedStop || selectedAttraction ? (
                                    <Stack gap="lg" pr="xs">
                                        {(selectedStopPool || selectedAttraction)?.attractionId != null && (
                                            <AttractionStopBriefCard
                                                key={(selectedStopPool || selectedAttraction)!.placeKey}
                                                brief={(selectedStopPool || selectedAttraction)?.stopBrief}
                                                routeId={routeId}
                                                title={attractionTitle(selectedStopPool || selectedAttraction, stopLang)}
                                            />
                                        )}
                                        {selectedStop && (
                                            <Switch
                                                label="Исключено при полевой проверке"
                                                checked={selectedStop.fieldExcluded === true}
                                                onChange={(e) => {
                                                    const value = e.currentTarget.checked;
                                                    setStops((prev) =>
                                                        prev.map((s) =>
                                                            s.key === selectedStop.key
                                                                ? { ...s, fieldExcluded: value }
                                                                : s,
                                                        ),
                                                    );
                                                    markDirty();
                                                }}
                                            />
                                        )}
                                        {selectedStop?.establishmentId && !textOnly ? (
                                            <Stack gap="xs">
                                                <Switch
                                                    label="Перерыв по желанию — без номера, аудио и прогресса"
                                                    checked={selectedStop.optionalBreak === true}
                                                    onChange={(event) => {
                                                        const checked = event.currentTarget.checked;
                                                        setStops((prev) =>
                                                            prev.map((s) =>
                                                                s.key === selectedStop.key
                                                                    ? {
                                                                          ...s,
                                                                          optionalBreak: checked,
                                                                          alternateStart: false,
                                                                          breakMinutes: checked
                                                                              ? s.breakMinutes
                                                                              : null,
                                                                      }
                                                                    : s,
                                                            ),
                                                        );
                                                        markDirty();
                                                    }}
                                                />
                                                {selectedStop.optionalBreak ? (
                                                    <NumberInput
                                                        label="Время на перерыв, мин"
                                                        min={0}
                                                        value={selectedStop.breakMinutes ?? ''}
                                                        onChange={(value) => {
                                                            setStops((prev) =>
                                                                prev.map((s) =>
                                                                    s.key === selectedStop.key
                                                                        ? {
                                                                              ...s,
                                                                              breakMinutes:
                                                                                  typeof value ===
                                                                                  'number'
                                                                                      ? value
                                                                                      : null,
                                                                          }
                                                                        : s,
                                                                ),
                                                            );
                                                            markDirty();
                                                        }}
                                                    />
                                                ) : (
                                                    <Text size="sm" c="dimmed">
                                                        Рассказ и аудио этого места заполняются
                                                        ниже. Точка на карте — где удобно слушать;
                                                        вход в заведение остаётся в его карточке.
                                                    </Text>
                                                )}
                                                <Group grow>
                                                    <NumberInput
                                                        label="Широта точки прослушивания"
                                                        min={-90}
                                                        max={90}
                                                        decimalScale={6}
                                                        value={selectedStop.lat ?? ''}
                                                        onChange={(value) => {
                                                            setStops((prev) =>
                                                                prev.map((s) =>
                                                                    s.key === selectedStop.key
                                                                        ? {
                                                                              ...s,
                                                                              lat:
                                                                                  typeof value ===
                                                                                  'number'
                                                                                      ? value
                                                                                      : null,
                                                                          }
                                                                        : s,
                                                                ),
                                                            );
                                                            markDirty();
                                                        }}
                                                    />
                                                    <NumberInput
                                                        label="Долгота точки прослушивания"
                                                        min={-180}
                                                        max={180}
                                                        decimalScale={6}
                                                        value={selectedStop.lng ?? ''}
                                                        onChange={(value) => {
                                                            setStops((prev) =>
                                                                prev.map((s) =>
                                                                    s.key === selectedStop.key
                                                                        ? {
                                                                              ...s,
                                                                              lng:
                                                                                  typeof value ===
                                                                                  'number'
                                                                                      ? value
                                                                                      : null,
                                                                          }
                                                                        : s,
                                                                ),
                                                            );
                                                            markDirty();
                                                        }}
                                                    />
                                                </Group>
                                            </Stack>
                                        ) : null}
                                        {selectedStop?.id ? (
                                            <StaffNotesPanel routeStopId={selectedStop.id} />
                                        ) : null}
                                        <Section
                                            boxed
                                            title={
                                                selectedStop
                                                    ? t(
                                                          'editors.route.constructorTab.placeSectionTitle',
                                                          { n: selectedStop.order + 1 },
                                                      )
                                                    : t('editors.route.constructorTab.place')
                                            }
                                            description={
                                                selectedStop?.kind ===
                                                ReadyRouteStopKindEnum.WAYPOINT
                                                    ? t(
                                                          'editors.route.constructorTab.waypointDescription',
                                                      )
                                                    : t(
                                                          'editors.route.constructorTab.selectedAttractionDescription',
                                                      )
                                            }
                                        >
                                            <Group
                                                justify="space-between"
                                                align="flex-start"
                                                wrap="nowrap"
                                                gap="sm"
                                            >
                                                <Group
                                                    align="flex-start"
                                                    wrap="nowrap"
                                                    gap="sm"
                                                    style={{ minWidth: 0, flex: 1 }}
                                                >
                                                    {selectedStop?.kind !==
                                                        ReadyRouteStopKindEnum.WAYPOINT && (
                                                        <AttractionPhotos
                                                            photos={
                                                                (
                                                                    selectedStopPool ||
                                                                    selectedAttraction
                                                                )?.photos ||
                                                                ((
                                                                    selectedStopPool ||
                                                                    selectedAttraction
                                                                )?.photoUrl
                                                                    ? [
                                                                          (selectedStopPool ||
                                                                              selectedAttraction)!
                                                                              .photoUrl!,
                                                                      ]
                                                                    : [])
                                                            }
                                                            title={attractionTitle(
                                                                selectedStopPool ||
                                                                    selectedAttraction,
                                                                stopLang,
                                                                selectedStop?.title,
                                                            )}
                                                        />
                                                    )}
                                                    <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                                                        <Text fw={600} size="sm">
                                                            {selectedStop
                                                                ? selectedStop.kind ===
                                                                  ReadyRouteStopKindEnum.WAYPOINT
                                                                    ? t(
                                                                          'editors.route.constructorTab.waypoint',
                                                                      )
                                                                    : attractionTitle(
                                                                          selectedStopPool,
                                                                          stopLang,
                                                                          selectedStop.title,
                                                                      ) ||
                                                                      `#${selectedStop.attractionId}`
                                                                : attractionTitle(
                                                                      selectedAttraction,
                                                                      stopLang,
                                                                  )}
                                                        </Text>
                                                        {(() => {
                                                            const knownFor = attractionKnownFor(
                                                                selectedStopPool ||
                                                                    selectedAttraction,
                                                                stopLang,
                                                            );

                                                            return knownFor ? (
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                    lineClamp={4}
                                                                >
                                                                    {knownFor}
                                                                </Text>
                                                            ) : null;
                                                        })()}
                                                        {selectedStop?.kind !==
                                                            ReadyRouteStopKindEnum.WAYPOINT &&
                                                            (selectedStop?.attractionId != null ||
                                                                selectedStop?.establishmentId !=
                                                                    null ||
                                                                selectedAttraction) && (
                                                                <Button
                                                                    size="xs"
                                                                    variant="light"
                                                                    mt={6}
                                                                    style={{
                                                                        alignSelf: 'flex-start',
                                                                    }}
                                                                    onClick={() =>
                                                                        setDetailsPlace(
                                                                            selectedStop ||
                                                                                selectedAttraction,
                                                                        )
                                                                    }
                                                                >
                                                                    {(
                                                                        selectedStop ||
                                                                        selectedAttraction
                                                                    )?.establishmentId != null
                                                                        ? 'Детали заведения'
                                                                        : 'Детали аттракшена'}
                                                                </Button>
                                                            )}
                                                    </Stack>
                                                </Group>
                                                {selectedAttraction &&
                                                    !selectedStop &&
                                                    !textOnly && (
                                                        <Button
                                                            size="xs"
                                                            onClick={() =>
                                                                addAttractionStop(
                                                                    selectedAttraction,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                'editors.route.constructorTab.addToRoute',
                                                            )}
                                                        </Button>
                                                    )}
                                            </Group>
                                            {selectedStop &&
                                            selectedStop.kind !== ReadyRouteStopKindEnum.WAYPOINT &&
                                            !textOnly ? (
                                                <>
                                                    <Select
                                                        mt="sm"
                                                        label={t(
                                                            'editors.route.constructorTab.routeRole',
                                                        )}
                                                        description={t(
                                                            'editors.route.constructorTab.routeRoleHint',
                                                        )}
                                                        data={enumOptions(
                                                            Object.values(AttractionRouteRoleEnum),
                                                            t,
                                                            'enums.attractionRouteRole',
                                                        )}
                                                        clearable
                                                        value={selectedStop.routeRoleOverride}
                                                        onChange={(value) => {
                                                            const key = selectedStop.key;

                                                            setStops((prev) =>
                                                                prev.map((stop) =>
                                                                    stop.key === key
                                                                        ? {
                                                                              ...stop,
                                                                              routeRoleOverride:
                                                                                  (value as AttractionRouteRoleEnum) ||
                                                                                  null,
                                                                          }
                                                                        : stop,
                                                                ),
                                                            );
                                                            markDirty();
                                                        }}
                                                    />
                                                    <Switch
                                                        mt="sm"
                                                        label={t(
                                                            'editors.route.constructorTab.alternateStart',
                                                        )}
                                                        description={t(
                                                            'editors.route.constructorTab.alternateStartHint',
                                                        )}
                                                        checked={
                                                            selectedStop.key !== primaryStartKey &&
                                                            selectedStop.alternateStart === true
                                                        }
                                                        disabled={
                                                            selectedStop.key === primaryStartKey ||
                                                            selectedStop.optionalBreak
                                                        }
                                                        onChange={(event) => {
                                                            const checked =
                                                                event.currentTarget.checked;
                                                            const key = selectedStop.key;

                                                            setStops((prev) =>
                                                                prev.map((stop) =>
                                                                    stop.key === key
                                                                        ? {
                                                                              ...stop,
                                                                              alternateStart:
                                                                                  checked,
                                                                          }
                                                                        : stop,
                                                                ),
                                                            );
                                                            markDirty();
                                                        }}
                                                    />
                                                </>
                                            ) : null}
                                        </Section>

                                        {selectedStop?.optionalBreak ? (
                                            <Text size="sm" c="dimmed">
                                                Перерыв показывает карточку заведения. Рассказ и
                                                аудио не нужны.
                                            </Text>
                                        ) : selectedStop?.kind ===
                                          ReadyRouteStopKindEnum.WAYPOINT ? (
                                            <Section
                                                boxed
                                                title={t('editors.route.constructorTab.textsTitle')}
                                            >
                                                <Text size="sm" c="dimmed">
                                                    {t(
                                                        'editors.route.constructorTab.noWaypointTexts',
                                                    )}
                                                </Text>
                                            </Section>
                                        ) : (
                                            <>
                                                <LanguageChips
                                                    languages={stopLocaleChips}
                                                    value={stopLang}
                                                    onChange={(v) => setStopLang(v as Languages)}
                                                />
                                                <Section
                                                    boxed
                                                    title={t(
                                                        'editors.route.constructorTab.textsTitle',
                                                    )}
                                                    description={
                                                        selectedStop
                                                            ? t(
                                                                  'editors.route.constructorTab.textsDescriptionOverride',
                                                              )
                                                            : t(
                                                                  'editors.route.constructorTab.textsDescriptionSource',
                                                              )
                                                    }
                                                >
                                                    {(() => {
                                                        const attractionText = poolLocale(
                                                            selectedStopPool || selectedAttraction,
                                                            stopLang,
                                                        );
                                                        const override = selectedStop
                                                            ? selectedStop.locales[stopLang] ||
                                                              emptyStopContent()
                                                            : null;
                                                        const stopAudio = selectedStop
                                                            ? selectedStop.audio[stopLang] ||
                                                              emptyStopAudio()
                                                            : null;
                                                        const hasAttraction =
                                                            !!attractionText?.hook?.trim() ||
                                                            !!attractionText?.guideTalk?.trim();

                                                        if (!selectedStop) {
                                                            if (!hasAttraction) {
                                                                return (
                                                                    <Text size="sm" c="dimmed">
                                                                        {t(
                                                                            'editors.route.constructorTab.noTextsInLang',
                                                                            {
                                                                                lang: stopLang.toUpperCase(),
                                                                            },
                                                                        )}
                                                                    </Text>
                                                                );
                                                            }

                                                            return (
                                                                <Stack gap="md">
                                                                    {attractionText?.hook?.trim() && (
                                                                        <Box>
                                                                            <Text
                                                                                size="xs"
                                                                                fw={600}
                                                                                mb={4}
                                                                            >
                                                                                {t(
                                                                                    'editors.route.constructorTab.hook',
                                                                                )}
                                                                            </Text>
                                                                            <Text
                                                                                size="sm"
                                                                                style={{
                                                                                    whiteSpace:
                                                                                        'pre-wrap',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    attractionText.hook
                                                                                }
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                    {attractionText?.guideTalk?.trim() && (
                                                                        <Box>
                                                                            <Text
                                                                                size="xs"
                                                                                fw={600}
                                                                                mb={4}
                                                                            >
                                                                                {t(
                                                                                    'editors.route.constructorTab.guideTalk',
                                                                                )}
                                                                            </Text>
                                                                            <Text
                                                                                size="sm"
                                                                                style={{
                                                                                    whiteSpace:
                                                                                        'pre-wrap',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    attractionText.guideTalk
                                                                                }
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                </Stack>
                                                            );
                                                        }

                                                        return (
                                                            <Tabs
                                                                value={textSourceTab}
                                                                onChange={(v) =>
                                                                    setTextSourceTab(
                                                                        (v as
                                                                            | 'route'
                                                                            | 'attraction') ||
                                                                            'route',
                                                                    )
                                                                }
                                                            >
                                                                <Tabs.List>
                                                                    <Tabs.Tab value="route">
                                                                        {t(
                                                                            'editors.route.constructorTab.routeTab',
                                                                        )}
                                                                    </Tabs.Tab>
                                                                    <Tabs.Tab value="attraction">
                                                                        {t(
                                                                            'editors.route.constructorTab.attractionTab',
                                                                        )}
                                                                    </Tabs.Tab>
                                                                </Tabs.List>

                                                                <Tabs.Panel value="route" pt="sm">
                                                                    <Stack gap="sm">
                                                                        <Textarea
                                                                            label={t(
                                                                                'editors.route.constructorTab.hook',
                                                                            )}
                                                                            minRows={3}
                                                                            autosize
                                                                            maxRows={10}
                                                                            value={
                                                                                override?.hook || ''
                                                                            }
                                                                            onChange={(e) =>
                                                                                patchStopContent(
                                                                                    selectedStop.key,
                                                                                    stopLang,
                                                                                    'hook',
                                                                                    e.currentTarget
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                        <Textarea
                                                                            label={t(
                                                                                'editors.route.constructorTab.guideTalk',
                                                                            )}
                                                                            minRows={5}
                                                                            autosize
                                                                            maxRows={16}
                                                                            value={
                                                                                override?.guideTalk ||
                                                                                ''
                                                                            }
                                                                            onChange={(e) =>
                                                                                patchStopContent(
                                                                                    selectedStop.key,
                                                                                    stopLang,
                                                                                    'guideTalk',
                                                                                    e.currentTarget
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                        {!textOnly && (
                                                                            <>
                                                                                <Divider
                                                                                    label={t(
                                                                                        'editors.route.constructorTab.audioSection',
                                                                                    )}
                                                                                    labelPosition="left"
                                                                                />
                                                                                <MediaDropzone
                                                                                    kind="audio"
                                                                                    allowUrl={false}
                                                                                    label={t(
                                                                                        'editors.route.constructorTab.guideAudio',
                                                                                    )}
                                                                                    value={
                                                                                        stopAudio?.guideAudioUrl
                                                                                    }
                                                                                    onChange={(
                                                                                        url,
                                                                                    ) =>
                                                                                        void patchStopAudio(
                                                                                            selectedStop.key,
                                                                                            stopLang,
                                                                                            url,
                                                                                        )
                                                                                    }
                                                                                />
                                                                                <Text
                                                                                    size="xs"
                                                                                    c="dimmed"
                                                                                >
                                                                                    {t(
                                                                                        'editors.route.constructorTab.audioHint',
                                                                                        {
                                                                                            guide: formatAudioLength(
                                                                                                stopAudio?.guideAudioDurationSec,
                                                                                            ),
                                                                                        },
                                                                                    )}
                                                                                </Text>
                                                                            </>
                                                                        )}
                                                                    </Stack>
                                                                </Tabs.Panel>

                                                                <Tabs.Panel
                                                                    value="attraction"
                                                                    pt="sm"
                                                                >
                                                                    {hasAttraction ? (
                                                                        <Stack gap="md">
                                                                            {attractionText?.hook?.trim() && (
                                                                                <Box>
                                                                                    <Text
                                                                                        size="xs"
                                                                                        fw={600}
                                                                                        mb={4}
                                                                                    >
                                                                                        {t(
                                                                                            'editors.route.constructorTab.hook',
                                                                                        )}
                                                                                    </Text>
                                                                                    <Text
                                                                                        size="sm"
                                                                                        style={{
                                                                                            whiteSpace:
                                                                                                'pre-wrap',
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            attractionText.hook
                                                                                        }
                                                                                    </Text>
                                                                                </Box>
                                                                            )}
                                                                            {attractionText?.guideTalk?.trim() && (
                                                                                <Box>
                                                                                    <Text
                                                                                        size="xs"
                                                                                        fw={600}
                                                                                        mb={4}
                                                                                    >
                                                                                        {t(
                                                                                            'editors.route.constructorTab.guideTalk',
                                                                                        )}
                                                                                    </Text>
                                                                                    <Text
                                                                                        size="sm"
                                                                                        style={{
                                                                                            whiteSpace:
                                                                                                'pre-wrap',
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            attractionText.guideTalk
                                                                                        }
                                                                                    </Text>
                                                                                </Box>
                                                                            )}
                                                                        </Stack>
                                                                    ) : (
                                                                        <Text size="sm" c="dimmed">
                                                                            {t(
                                                                                'editors.route.constructorTab.noAttractionTexts',
                                                                                {
                                                                                    lang: stopLang.toUpperCase(),
                                                                                },
                                                                            )}
                                                                        </Text>
                                                                    )}
                                                                </Tabs.Panel>
                                                            </Tabs>
                                                        );
                                                    })()}
                                                </Section>
                                            </>
                                        )}
                                    </Stack>
                                ) : (
                                    <Text size="sm" c="dimmed">
                                        {t('editors.route.constructorTab.selectStopOrPoint')}
                                    </Text>
                                )}
                            </Box>
                        </Box>
                    )}
                </Stack>
            )}
            {activeTab === 'history' && (routeId
                ? <FieldHistoryPanel title="История всего маршрута" hideEmpty={false} target={{ kind: 'route', id: routeId }} />
                : <Text c="dimmed">История будет доступна после сохранения маршрута.</Text>
            )}
            {opened && detailsPlace?.attractionId != null && (
                <AttractionEditor
                    opened
                    zIndex={300}
                    attractionId={detailsPlace.attractionId}
                    onClose={() => setDetailsPlace(null)}
                    onSaved={() => {
                        if (cityId != null)
                            void loadPool(cityId).catch((error) =>
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                }),
                            );
                    }}
                    onDeleted={() => {
                        setDetailsPlace(null);
                        if (cityId != null)
                            void loadPool(cityId).catch((error) =>
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                }),
                            );
                    }}
                />
            )}
            {opened && detailsPlace?.establishmentId != null && (
                <EstablishmentEditor
                    opened
                    zIndex={300}
                    establishmentId={detailsPlace.establishmentId}
                    onClose={() => setDetailsPlace(null)}
                    onSaved={() => {
                        if (cityId != null)
                            void loadPool(cityId).catch((error) =>
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                }),
                            );
                    }}
                    onDeleted={() => {
                        setDetailsPlace(null);
                        if (cityId != null)
                            void loadPool(cityId).catch((error) =>
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                }),
                            );
                    }}
                />
            )}
        </EntityEditorShell>
    );
};
